import {useEffect, useMemo, useState} from 'react';
import {useAuth} from './auth';
import {AuditPage} from './AuditPage';
import {Login} from './Login';
import {Notice, Spinner} from './components';
import {Capacity} from './components/Capacity';
import {ContextColumn, useWideLayout} from './components/ContextColumn';
import {Icon, type IconName} from './components/Icon';
import {IdentityCell} from './components/IdentityCell';
import {runtimeConfig} from './config';
import {ProfilesPage} from './ProfilesPage';
import {UsersPage} from './UsersPage';

type View = 'users' | 'profiles' | 'audit';

const views: Array<{id: View; label: string; permission: string; icon: IconName}> = [
  {id: 'users', label: 'Utilisateurs', permission: 'iam.users.read', icon: 'users'},
  {id: 'profiles', label: 'Profils et permissions', permission: 'iam.profiles.read', icon: 'shield'},
  {id: 'audit', label: 'Historique', permission: 'iam.audit.read', icon: 'history'},
];

export default function App() {
  const {user, me, loading, error, logOut, refreshAccess, pendingMfa, finishMfa} = useAuth();
  const available = useMemo(() => views.filter(view => me?.effectivePermissions.some(grant => grant.permission === view.permission)), [me]);
  const [view, setView] = useState<View>('users');
  const [mfaCode, setMfaCode] = useState('');
  const wide = useWideLayout();

  useEffect(() => {
    if (available.length && !available.some(item => item.id === view)) setView(available[0].id);
  }, [available, view]);

  if (loading) return <main className="center"><Spinner label="Ouverture de la session…" /></main>;
  if (!user) return <Login />;
  if (!me) return <main className="center"><section className="login-card"><h1>Accès indisponible</h1><Notice tone="danger">{error || 'Le service des accès ne répond pas.'}</Notice><div className="actions"><button className="btn" onClick={() => void logOut()}>Se déconnecter</button><button className="btn primary" onClick={() => void refreshAccess()}>Réessayer</button></div></section></main>;

  const title = views.find(item => item.id === view)?.label || 'Accès';
  const identity = {uid: user.uid, email: user.email, displayName: user.displayName, resolved: true};

  return <>
    <a className="skip" href="#main">Aller au contenu</a>
    <div className="app-shell">
      <aside className="rail">
        <div className="brand"><div className="brand-mark">GD</div><div className="brand-text"><strong>Goat Deeper</strong><span>Accès</span></div></div>
        <nav aria-label="Navigation principale">
          {available.map(item => <button key={item.id} type="button" className="nav-link" title={item.label} aria-current={view === item.id ? 'page' : undefined} onClick={() => setView(item.id)}>
            <Icon name={item.icon} size={18} /><span>{item.label}</span>
          </button>)}
        </nav>
        <div className="rail-foot">
          <span className="env">{runtimeConfig.authEmulatorUrl ? 'ÉMULATEUR' : 'PRODUCTION'}</span>
          <IdentityCell identity={identity} />
          <button type="button" className="btn sm" title="Se déconnecter" onClick={() => void logOut()}><Icon name="logout" size={13} /><span> Se déconnecter</span></button>
        </div>
      </aside>
      <div className="workspace">
        <header className="topbar"><h1>{title}</h1><Capacity compact /></header>
        {!wide && <ContextColumn folded />}
        <main className="main" id="main" tabIndex={-1}>
          {me.status !== 'active' ? <Notice tone="warning">Ce compte est {me.status === 'suspended' ? 'suspendu' : 'sans accès staff'}. Les vues protégées ne sont pas disponibles.</Notice>
            : !available.length ? <Notice tone="warning">Aucune permission de lecture n’est attribuée à ce compte.</Notice> : <>
              {view === 'users' && <UsersPage />}
              {view === 'profiles' && <ProfilesPage />}
              {view === 'audit' && <AuditPage />}
            </>}
        </main>
      </div>
      {wide && <ContextColumn />}
    </div>
    {pendingMfa && <div className="modal-backdrop" role="presentation"><form className="modal" role="dialog" aria-modal="true" aria-labelledby="mfa-title" onSubmit={async event => {event.preventDefault(); try { await finishMfa(mfaCode); setMfaCode(''); } catch { /* Le contexte affiche l'erreur Firebase. */ }}}><p className="eyebrow">Vérification renforcée</p><h2 id="mfa-title">Code envoyé vers {pendingMfa.phoneLabel}</h2>{error && <Notice tone="danger">{error}</Notice>}<label>Code MFA<input inputMode="numeric" autoComplete="one-time-code" value={mfaCode} onChange={event => setMfaCode(event.target.value)} required autoFocus /></label><button className="btn primary">Vérifier</button></form></div>}
  </>;
}
